import type { DistribuicaoMensal } from '@favo/core';

import { formatarMoeda, formatarNumero } from '@/design/formato';

import { fraseEquilibrio, MES } from './frases';
import { abreviarReais, formatarVolume } from './grandezas';

/* Geometria comum aos dois gráficos. Desenhados à mão em SVG porque o
 * documento é impresso: sem biblioteca, sem canvas e sem fonte externa, o que
 * aparece na tela é exatamente o que sai no PDF.
 *
 * DOIS gráficos e não um: volume está em PROJETOS e a curva em REAIS, e
 * empilhar as duas grandezas num plano só exigiria dois eixos verticais. Dois
 * eixos numa figura inventam correlação que o dado não tem.
 *
 * COR: a curva do preço atual é fato (tinta neutra) e a do preço mínimo é a
 * proposta (--acao). A despesa fixa é LIMIAR, não série, então é tracejada na
 * família do mel (--cascata-3), que é o campo do que sai. O par
 * --acao × --cascata-3 foi validado contra os três tipos de daltonismo (pior
 * separação ΔE 9,9 em protanopia) e as três marcas ainda se distinguem por
 * forma e por luminosidade, que é o que salva a impressão em preto e branco. */
const AREA = { L: 56, R: 16, T: 14, B: 26, W: 460, H: 200 };

export function GraficoVolume({ dist }: { dist: DistribuicaoMensal }) {
  const { L, R, T, B, W, H } = AREA;
  const larguraUtil = W - L - R;
  const alturaUtil = H - T - B;

  const projetos = dist.linhas.map((l) => l.projetos);
  const teto = Math.max(...projetos, 0);
  const escala = teto > 0 ? teto * 1.15 : 1;

  const passo = larguraUtil / 12;
  const largura = Math.max(passo - 2, 1); // 2px de parede entre barras vizinhas
  const yDe = (v: number) => T + alturaUtil - (v / escala) * alturaUtil;
  const marcas = [0, 1, 2].map((i) => (escala * i) / 2);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="svg"
      role="img"
      aria-label={`Projetos por mês ao longo do ano, ${formatarVolume(
        projetos.reduce((soma, v) => soma + v, 0),
      )} no total.`}
    >
      {marcas.map((valor) => (
        <g key={valor}>
          <line className="grade" x1={L} y1={yDe(valor)} x2={W - R} y2={yDe(valor)} />
          <text
            className="rotulo-eixo"
            x={L - 8}
            y={yDe(valor)}
            textAnchor="end"
            dominantBaseline="middle"
          >
            {formatarNumero(valor, { casasMax: 1 })}
          </text>
        </g>
      ))}

      {dist.linhas.map((l) => {
        const altura = T + alturaUtil - yDe(l.projetos);
        if (!(altura > 0)) return null;
        return (
          <path
            key={l.mes}
            className="barra"
            d={caminhoBarra(L + l.mes * passo + 1, yDe(l.projetos), largura, altura, 4)}
          />
        );
      })}

      {dist.linhas.map((l) => (
        <text
          key={l.mes}
          className="rotulo-eixo"
          x={L + l.mes * passo + 1 + largura / 2}
          y={H - 8}
          textAnchor="middle"
        >
          {MES[l.mes]}
        </text>
      ))}
    </svg>
  );
}

export function GraficoEquilibrio({
  dist,
  distSugerido,
}: {
  dist: DistribuicaoMensal;
  distSugerido: DistribuicaoMensal | null;
}) {
  const { L, R, T, B, W, H } = AREA;
  const larguraUtil = W - L - R;
  const alturaUtil = H - T - B;

  const serieAtual = dist.linhas.map((l) => l.mcAcumulada);
  const serieMinimo = distSugerido ? distSugerido.linhas.map((l) => l.mcAcumulada) : null;
  const todos = [0, dist.fixasAno, ...serieAtual, ...(serieMinimo ?? [])];

  const piso = Math.min(...todos);
  const teto = Math.max(...todos);
  const faixa = teto - piso > 0 ? (teto - piso) * 1.12 : 1;

  const xDe = (i: number) => L + (i / 11) * larguraUtil;
  const yDe = (v: number) => T + alturaUtil - ((v - piso) / faixa) * alturaUtil;
  const caminho = (serie: number[]) =>
    serie.map((v, i) => `${i === 0 ? 'M' : 'L'}${xDe(i).toFixed(1)},${yDe(v).toFixed(1)}`).join(' ');

  const marcas = [0, 1, 2, 3].map((i) => piso + (faixa * i) / 3);
  const yFixa = yDe(dist.fixasAno);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="svg"
      role="img"
      aria-label={`Margem de contribuição acumulada mês a mês contra a despesa fixa do ano, de ${formatarMoeda(
        dist.fixasAno,
      )}. ${fraseEquilibrio(dist, 'ao preço atual')}`}
    >
      {marcas.map((valor) => (
        <g key={valor}>
          <line className="grade" x1={L} y1={yDe(valor)} x2={W - R} y2={yDe(valor)} />
          <text
            className="rotulo-eixo"
            x={L - 8}
            y={yDe(valor)}
            textAnchor="end"
            dominantBaseline="middle"
          >
            {abreviarReais(valor)}
          </text>
        </g>
      ))}

      {dist.fixasAno > 0 ? (
        <>
          <line className="linha-fixa" x1={L} y1={yFixa} x2={W - R} y2={yFixa} />
          <text className="rotulo-limiar" x={L} y={yFixa - 6} textAnchor="start">
            Despesa fixa do ano
          </text>
        </>
      ) : null}

      <path className="curva-atual" d={caminho(serieAtual)} />
      {serieMinimo ? <path className="curva-minimo" d={caminho(serieMinimo)} /> : null}

      {dist.mesEquilibrio !== null ? (
        <circle className="ponto-atual" cx={xDe(dist.mesEquilibrio)} cy={yFixa} r={5} />
      ) : null}
      {distSugerido && distSugerido.mesEquilibrio !== null ? (
        <circle className="ponto-minimo" cx={xDe(distSugerido.mesEquilibrio)} cy={yFixa} r={5} />
      ) : null}

      {dist.linhas.map((l) => (
        <text key={l.mes} className="rotulo-eixo" x={xDe(l.mes)} y={H - 8} textAnchor="middle">
          {MES[l.mes]}
        </text>
      ))}
    </svg>
  );
}

/** Barra com as duas quinas de cima arredondadas e a base encostada no eixo. */
function caminhoBarra(x: number, y: number, largura: number, altura: number, raio: number): string {
  const r = Math.max(0, Math.min(raio, largura / 2, altura));
  return [
    `M${x},${y + altura}`,
    `L${x},${y + r}`,
    `Q${x},${y} ${x + r},${y}`,
    `L${x + largura - r},${y}`,
    `Q${x + largura},${y} ${x + largura},${y + r}`,
    `L${x + largura},${y + altura}`,
    'Z',
  ].join(' ');
}
